import React, { useState, useEffect, useMemo } from 'react';
import { Button, Form, Alert, ProgressBar, Card } from 'react-bootstrap';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import { useMembership } from '../../context/MembershipContext';
import { createArticle, ARTICLE_IMPORTANCE, assertCanMarkArticleEssential } from '../../services/articlesService';
import {
  ARTICLE_SKUPINA_FIELD_HELP,
  buildArticleSkupinaOptions,
  skupinaValueToGrouping,
} from '../../services/articleListGroups';
import { fetchUnitsForTenant } from '../../services/unitsService';
import ContentAttachmentsEditor from '../../platform/ContentAttachmentsEditor';
import './ArticleCreate.css';
import { useToast } from '../../platform/toast/ToastProvider';

function ArticleCreate({ onArticleCreated }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [newFiles, setNewFiles] = useState([]);
  const [unit, setUnit] = useState('general');
  const [unitName, setUnitName] = useState('Splošno');
  const [skupinaValue, setSkupinaValue] = useState('other');
  const [units, setUnits] = useState([]);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [thumbnail, setThumbnail] = useState(null);

  const { user } = useUser();
  const { tenantId, config } = useTenant();
  const { memberships } = useMembership();
  const toast = useToast();

  const skupinaOptions = useMemo(
    () => buildArticleSkupinaOptions(config),
    [config],
  );

  useEffect(() => {
    if (!user || !tenantId) return;
    fetchUnitsForTenant(tenantId, { user, memberships }).then(setUnits);
  }, [user, tenantId, memberships]);

  const handleUnitChange = (e) => {
    const selectedUnitId = e.target.value;
    const selectedUnit = units.find((u) => u.id === selectedUnitId);
    setUnit(selectedUnitId);
    setUnitName(selectedUnit ? selectedUnit.unitName : 'Splošno');
  };

  const handleThumbnailChange = (e) => {
    const selectedImage = e.target.files[0];
    if (selectedImage && !selectedImage.type.startsWith('image/')) {
      setError('Za ikono so dovoljene samo slikovne datoteke.');
      setThumbnail(null);
    } else {
      setError('');
      setThumbnail(selectedImage || null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      setError('Za nalaganje obrazca morate biti prijavljeni.');
      return;
    }
    if (!title.trim() || !description.trim()) {
      setError('Naslov in opis sta obvezna.');
      return;
    }

    const { listGroupId, importance } = skupinaValueToGrouping(skupinaValue);

    setUploading(true);
    setError('');

    try {
      if (importance === ARTICLE_IMPORTANCE.ESSENTIAL) {
        await assertCanMarkArticleEssential(tenantId);
      }

      await createArticle(user, {
        activeTenantId: tenantId,
        title: title.trim(),
        description: description.trim(),
        files: newFiles,
        thumbnail,
        unit,
        unitName,
        listGroupId,
        importance,
        onProgress: setUploadProgress,
      });

      toast.success('Obrazec je bil objavljen.');
      onArticleCreated();
    } catch (uploadError) {
      console.error('Error uploading article:', uploadError);
      const message = uploadError.message || 'Nalaganje obrazca ni uspelo.';
      setError(message);
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="article-create">
      {error && <Alert variant="danger">{error}</Alert>}

      <Form onSubmit={handleSubmit}>
        <Card className="article-create-card platform-content-detail-card">
          <Card.Header className="article-create-card__header">
            <h2 className="article-create-card__title">Nov obrazec</h2>
            <p className="article-create-card__subtitle">
              Naložite predlogo ali obrazec z opcijsko ikono in eno ali več PDF datotek.
            </p>
          </Card.Header>
          <Card.Body className="article-create-card__body">
            <Form.Group className="article-create-field">
              <Form.Label>Naslov</Form.Label>
              <Form.Control
                type="text"
                placeholder="Vnesite naslov obrazca"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                className="article-create-title-input"
              />
            </Form.Group>

            <Form.Group className="article-create-field">
              <Form.Label>Opis</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Kratek opis namena obrazca"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </Form.Group>

            <Form.Group className="article-create-field">
              <Form.Label>Skupina</Form.Label>
              <Form.Select
                value={skupinaValue}
                onChange={(event) => setSkupinaValue(event.target.value)}
              >
                {skupinaOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted article-create-field__hint">
                {ARTICLE_SKUPINA_FIELD_HELP}
              </Form.Text>
            </Form.Group>

            <Form.Group className="article-create-field">
              <Form.Label>Enota</Form.Label>
              <Form.Select value={unit} onChange={handleUnitChange}>
                <option value="general">Splošno</option>
                {units.map((unitObj) => (
                  <option key={unitObj.id} value={unitObj.id}>
                    {unitObj.unitName}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <ContentAttachmentsEditor
              label="PDF datoteke (neobvezno)"
              accept="application/pdf,.pdf"
              pdfOnly
              existingAttachments={[]}
              onExistingAttachmentsChange={() => {}}
              newFiles={newFiles}
              onNewFilesChange={setNewFiles}
              hint="Dodate lahko več PDF datotek hkrati."
            />

            <Form.Group className="article-create-field">
              <Form.Label>Slika (neobvezno)</Form.Label>
              <Form.Text className="text-muted article-create-field__hint">
                Neobvezno — prikaže se pod vsebino obrazca.
              </Form.Text>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleThumbnailChange}
              />
            </Form.Group>

            {uploading && (
              <ProgressBar
                now={uploadProgress}
                label={`${Math.round(uploadProgress)}%`}
                striped
                className="article-create-progress"
              />
            )}

            <Button variant="primary" type="submit" disabled={uploading}>
              {uploading ? 'Nalagam…' : 'Dodaj obrazec'}
            </Button>
          </Card.Body>
        </Card>
      </Form>
    </div>
  );
}

export default ArticleCreate;
